<!--
  MY JOVII — the three things that are yours: what you are in the middle of, what
  you kept, and what those two suggest.

  "Continue watching" IS the existing resume data (`jt.progress.v1`), not a new
  idea of the same thing. Every position in it was written by the player through
  `recordProgress`, so this screen and the row on Home always agree — there is
  one owner of that number and it is not this file.

  SAVING AND UNSAVING ARE THE SAME CONTROL EVERYWHERE — and that control lives
  on the TITLE PAGE. Tiles used to wear a bookmark overlay of their own; the
  owner called it tacky (2026-08-16) and it is gone, so the save on a title's
  page is what puts it in this shelf and what takes it back out again. This
  screen used to draw a second, different remove button of its own; two
  controls for one fact is how you end up with two answers to it.

  Suggestions are derived on the device from the genres of what you saved and
  opened — `taste()` in `shelf.ts`, the same function Home uses — and they are
  honest about it: each rail says which title it came from. Nothing is sent
  anywhere to produce them.
-->
<script lang="ts">
  import { tick } from 'svelte';
  import type { Season } from '$types';
  import Art from '$lib/ui/Art.svelte';
  import Sheet from '$lib/ui/Sheet.svelte';
  import Button from '$lib/ui/Button.svelte';
  import ContinueCard from '$lib/ui/ContinueCard.svelte';
  import Empty from '$lib/ui/Empty.svelte';
  import LazyRail from '$lib/ui/LazyRail.svelte';
  import Poster from '$lib/ui/Poster.svelte';
  import TileGrid from '$lib/ui/TileGrid.svelte';
  import Rail from '$lib/ui/Rail.svelte';
  import { continueWatching, forgetProgress, progress, recent, saved, type Progress } from '$lib/ui/prefs.svelte';
  import { backgroundUrl, detail, posterUrl } from '$lib/ui/cinemeta';
  import { episodeCode, remaining } from '$lib/ui/format';
  import { titleHref, watchHref } from '$lib/ui/nav.svelte';
  import { groupHistory, historyContinuation, type HistoryGroup } from '$lib/ui/history-groups';
  import { taste } from '$lib/ui/shelf';

  // Group before limiting: a run of episodes must not crowd other titles off the rail.
  const history = $derived(groupHistory(progress.items, continueWatching(Infinity)));
  const watching = $derived(history.filter((group) => group.resume).slice(0, 18));
  const previous = $derived(history.filter((group) => !group.resume));
  const likes = $derived(taste(saved.items, recent.items, 3));
  let selectedKey = $state<string | null>(null);
  const selected = $derived(history.find((group) => group.key === selectedKey) ?? null);
  let seasons = $state<Season[]>([]);
  let metadataLoading = $state(false);
  let metadataError = $state(false);
  let retry = $state(0);
  const continuation = $derived(selected ? historyContinuation(selected, seasons) : null);
  let trigger: HTMLElement | null = null;
  let historyHeading: HTMLHeadingElement | undefined;
  let removal = $state<{ keys: string[]; title: string; all: boolean } | null>(null);
  let removalTrigger: HTMLElement | null = null;

  function openHistory(group: HistoryGroup, event: MouseEvent): void {
    trigger = event.currentTarget as HTMLElement;
    selectedKey = group.key;
  }

  async function closeHistory(): Promise<void> {
    selectedKey = null;
    await tick();
    (trigger?.isConnected ? trigger : historyHeading)?.focus();
  }

  $effect(() => {
    const id = selected?.latest.imdbId;
    const needsNext = selected && !selected.resume;
    void retry;
    seasons = [];
    metadataError = false;
    metadataLoading = false;
    if (!id || !needsNext) return;
    const abort = new AbortController();
    let live = true;
    metadataLoading = true;
    void detail('series', id, abort.signal).then((data) => {
      if (live) seasons = data.seasons;
    }).catch(() => {
      if (live) metadataError = true;
    }).finally(() => {
      if (live) metadataLoading = false;
    });
    return () => { live = false; abort.abort(); };
  });

  function confirmRemoval(entries: Progress[], title: string, event: MouseEvent, all = false): void {
    removalTrigger = event.currentTarget as HTMLElement;
    removal = { keys: entries.map((entry) => entry.key), title, all };
  }

  async function cancelRemoval(): Promise<void> {
    removal = null;
    await tick();
    if (removalTrigger?.isConnected) removalTrigger.focus();
  }

  async function removeHistory(): Promise<void> {
    const request = removal;
    if (!request) return;
    removal = null;
    await tick();
    // Keep the existing per-resume-point deletion contract, including disk persistence.
    for (const key of request.keys) forgetProgress(key);
    await tick();
    if (selectedKey && !selected) await closeHistory();
    else if (removalTrigger?.isConnected) removalTrigger.focus();
    else if (selected) document.querySelector<HTMLElement>('dialog[open] .episode-history a')?.focus();
    else historyHeading?.focus();
  }

  function episodeLabel(item: Progress): string {
    return episodeCode(item.season ?? 1, item.episode ?? 1);
  }
</script>

<!-- "My Jovii" everywhere words face a person: the nav already says it, and two
     spellings of one destination read as two destinations. File names keep the
     old spelling; renames churn imports for no reader's benefit. -->
<svelte:head><title>My Jovii | Jovii</title></svelte:head>

<div class="page">
  <header class="head">
    <h1>My Jovii</h1>
    <p>Your next watch is right where you left it.</p>
  </header>

  {#if watching.length > 0}
    <!-- Same resume points as Home, grouped by show before display. -->
    <Rail title="Continue watching" wide>
      {#each watching as group, i (group.key)}
        {#if group.latest.kind === 'series'}
          {@render showCard(group, i < 2)}
        {:else if group.resume}
          <ContinueCard item={group.resume} eager={i < 2} />
        {/if}
      {/each}
    </Rail>
  {:else}
    <section>
      <h2>Continue watching</h2>
      <Empty
        icon="play"
        title="Nothing on the go"
        detail="Start a film or series and we’ll keep your place on this device."
      >
        <Button href="/" variant="primary" icon="search">Find something</Button>
      </Empty>
    </section>
  {/if}

  <section>
    <div class="history-heading">
      <h2 bind:this={historyHeading} tabindex="-1">Previously watched</h2>
      {#if history.length > 0}
        <Button variant="ghost" onclick={(event) => confirmRemoval(progress.items, 'all titles', event, true)}>Clear history</Button>
      {/if}
    </div>
    {#if previous.length > 0}
      <div class="history-grid">
        {#each previous as group (group.key)}
          {#if group.latest.kind === 'series'}
            {@render showCard(group)}
          {:else}
            <div class="history-movie">
              <a class="history-card" href={watchHref(group.latest)} aria-label={`Watch ${group.latest.title} again`}>
                <Art src={group.latest.background ?? backgroundUrl(group.latest.imdbId)} fallbacks={[group.latest.poster ?? posterUrl(group.latest.imdbId)]} alt="" label={group.latest.title} ratio="16 / 9" />
                <span class="history-name">{group.latest.title}</span>
                <span class="history-meta">Watched · Watch again</span>
              </a>
              <Button variant="ghost" title={`Remove ${group.latest.title} from history`} onclick={(event) => confirmRemoval(group.entries, group.latest.title, event)}>Remove</Button>
            </div>
          {/if}
        {/each}
      </div>
    {:else}
      <p class="history-note">{watching.length ? 'Shows in progress are in Continue watching. Open a show to see its watched episodes.' : 'Finished films and shows will appear here.'}</p>
    {/if}
  </section>

  <section>
    <h2>
      Saved
      {#if saved.items.length > 0}<span class="count">{saved.items.length}</span>{/if}
    </h2>
    {#if saved.items.length > 0}
      <!-- TileGrid: the rail's focus/strip/tap manners on a wrapping grid. -->
      <TileGrid>
        {#each saved.items as item, i (item.id)}
          <Poster {item} eager={i < 6} />
        {/each}
      </TileGrid>
      <p class="hint">Your list stays on this device. Save or unsave a title from its details page.</p>
    {:else}
      <!-- One action on the page, not one per empty block: when both lists are
           empty the two blocks sit a scroll apart, and twin "Find something"
           buttons would read as two features. The action rides the first. -->
      <Empty
        icon="bookmark"
        title="Nothing saved yet"
        detail="Save a title that catches your eye. It’ll be here when you’re ready."
      />
    {/if}
  </section>

  {#if likes.length > 0}
    {#each likes as like (like.kind + like.genre)}
      <LazyRail
        title={like.kind === 'series' ? `${like.genre} series` : `${like.genre} films`}
        note="because you kept {like.because}"
        kind={like.kind}
        catalog="top"
        genre={like.genre}
      />
    {/each}
  {:else}
    <!-- Nothing kept and nothing opened: there is no taste to read, so this says
         what is popular rather than inventing a reason. -->
    <LazyRail title="Popular films" kind="movie" catalog="top" />
    <LazyRail title="Popular series" kind="series" catalog="top" />
  {/if}
</div>

{#snippet showCard(group: HistoryGroup, eager = false)}
  {@const item = group.resume ?? group.latest}
  <button type="button" class="history-card" aria-haspopup="dialog" aria-label={`View watched episodes of ${item.title}`} onclick={(event) => openHistory(group, event)}>
    <div class="history-art">
      <Art src={item.background ?? backgroundUrl(item.imdbId)} fallbacks={[item.poster ?? posterUrl(item.imdbId)]} alt="" label={item.title} ratio="16 / 9" {eager} />
      {#if group.resume && item.durationSec > 0}
        <span class="history-progress" aria-hidden="true"><i style:width={`${Math.min(100, item.positionSec / item.durationSec * 100)}%`}></i></span>
      {/if}
    </div>
    <span class="history-name">{item.title}</span>
    <span class="history-meta">{group.entries.length} {group.entries.length === 1 ? 'episode' : 'episodes'} in history</span>
    {#if group.resume}<span class="history-meta">Continue {episodeLabel(item)} · {remaining(item.positionSec, item.durationSec) || 'Just started'}</span>{/if}
  </button>
{/snippet}

{#if selected}
  <Sheet open title={selected.latest.title} caption="Watched episodes, in season order" onclose={() => void closeHistory()}>
    <div class="history-actions">
      {#if continuation}
        <Button variant="primary" icon="play" href={watchHref(continuation)}>Continue watching · {episodeLabel(continuation)}</Button>
      {:else if metadataLoading}
        <p role="status">Checking the next episode…</p>
      {:else if metadataError}
        <p role="status">Could not check the next episode.</p>
        <Button onclick={() => retry += 1}>Try again</Button>
      {:else}
        <p>No next episode is listed.</p>
      {/if}
      <Button href={titleHref('series', selected.latest.imdbId)}>All episodes</Button>
    </div>
    <ol class="episode-history">
      {#each selected.entries as item (item.key)}
        <li>
          <a href={watchHref(item)} aria-label={`${item.finished ? 'Watch again' : 'Resume'} ${episodeLabel(item)}${item.subtitle ? `, ${item.subtitle}` : ''}`}>
            <span class="history-name">{episodeLabel(item)}{#if item.subtitle} · {item.subtitle}{/if}</span>
            <span class="history-meta">{item.finished ? 'Watched · Watch again' : `In progress · ${remaining(item.positionSec, item.durationSec) || 'Just started'}`}</span>
            {#if item.durationSec > 0}<progress max="100" value={Math.min(100, item.positionSec / item.durationSec * 100)} aria-label={`${episodeLabel(item)} playback progress`}></progress>{/if}
          </a>
          <Button variant="ghost" title={`Remove ${episodeLabel(item)} from history`} onclick={(event) => confirmRemoval([item], `${item.title} (${episodeLabel(item)})`, event)}>Remove</Button>
        </li>
      {/each}
    </ol>
    {#snippet footer()}
      <Button variant="ghost" onclick={(event) => selected && confirmRemoval(progress.items.filter((item) => item.kind === 'series' && item.imdbId === selected.latest.imdbId), selected.latest.title, event)}>Remove show from history</Button>
    {/snippet}
  </Sheet>
{/if}

{#if removal}
  <Sheet open title={removal.all ? 'Clear watch history?' : 'Remove from history?'} onclose={() => void cancelRemoval()}>
    <p class="history-confirm">This clears the saved playback positions and watched episodes for <strong>{removal.title}</strong> on this device. Saved titles stay in your list.</p>
    {#snippet footer()}
      <div class="history-actions">
        <Button onclick={() => void cancelRemoval()}>Cancel</Button>
        <button type="button" class="history-remove" onclick={removeHistory}>{removal?.all ? 'Clear history' : 'Remove'}</button>
      </div>
    {/snippet}
  </Sheet>
{/if}

<style>
  .page {
    padding-bottom: var(--space-6);
  }
  .head {
    padding: var(--space-5) max(var(--page-pad), var(--safe-r)) 0 max(var(--page-pad), var(--safe-l));
  }
  h1 {
    font-size: clamp(1.8rem, 3.2vw, 2.5rem);
    line-height: 1.15;
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
  }
  .head p {
    margin-top: var(--space-1);
    color: var(--text-dim);
    font-size: var(--text-sm);
  }

  section {
    padding: var(--space-5) max(var(--page-pad), var(--safe-r)) 0
      max(var(--page-pad), var(--safe-l));
  }
  h2 {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
    margin-bottom: var(--space-3);
    font-size: var(--text-lg);
    font-weight: var(--weight-semi);
    letter-spacing: var(--tracking-snug);
  }
  .count {
    color: var(--text-faint);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium);
  }

  /* The saved grid's recipe lives in TileGrid.svelte now. */
  .hint {
    margin-top: var(--space-3);
    color: var(--text-faint);
    font-size: var(--text-xs);
  }

  .history-heading, .history-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-3);
  }
  .history-heading { justify-content: space-between; margin-bottom: var(--space-3); }
  .history-heading h2 { margin: 0; }
  .history-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr));
    gap: var(--space-4);
  }
  .history-card {
    display: block;
    width: 100%;
    min-width: 0;
    text-align: start;
    border-radius: var(--radius);
    touch-action: manipulation;
  }
  .history-art {
    position: relative;
    overflow: hidden;
    border-radius: var(--radius);
    transition: transform var(--dur) var(--ease), box-shadow var(--dur) var(--ease);
  }
  .history-card:hover .history-art { transform: translateY(-2px); box-shadow: var(--shadow-2); }
  .history-card:focus-visible, .episode-history a:focus-visible, .history-remove:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 3px;
  }
  .history-name { display: block; margin-top: var(--space-2); font-weight: var(--weight-semi); overflow-wrap: anywhere; }
  .history-meta { display: block; margin-top: var(--space-1); font-size: var(--text-sm); color: var(--text-dim); overflow-wrap: anywhere; }
  .history-progress { position: absolute; inset: auto 0 0; height: 4px; background: var(--surface-3); }
  .history-progress i { display: block; height: 100%; background: var(--accent); }
  .history-note, .history-confirm { color: var(--text-dim); line-height: var(--leading); }
  .history-confirm { padding: var(--space-3) var(--space-4); overflow-wrap: anywhere; }
  .history-actions { padding: var(--space-3) var(--space-4); }
  .history-actions :global(.btn) { height: auto; min-height: var(--hit); white-space: normal; padding-block: var(--space-2); }
  .episode-history { list-style: none; padding: 0 var(--space-4); }
  .episode-history li { display: flex; align-items: center; gap: var(--space-2); padding-block: var(--space-3); border-top: 1px solid var(--line); }
  .episode-history a { flex: 1; min-width: 0; min-height: var(--hit); border-radius: var(--radius-xs); }
  .episode-history .history-name { margin-top: 0; }
  .episode-history progress { display: block; width: 100%; height: 4px; margin-top: var(--space-2); accent-color: var(--accent); }
  .history-remove { min-height: var(--hit); padding: var(--space-2) var(--space-4); border: 1px solid var(--bad); border-radius: var(--radius-pill); color: var(--bad); touch-action: manipulation; }
  .history-remove:hover { background: var(--surface-2); }
  @media (prefers-reduced-motion: reduce) {
    .history-art { transition: none; }
    .history-card:hover .history-art { transform: none; }
  }
</style>
